import React, { Dispatch, SetStateAction, useState } from 'react';
import { useContextSelector } from 'use-context-selector';
import { CatalogContext } from '@context/catalog.context';
import CurrentRefinements from '@components/catalog/current-refinements/current-refinements';
import { ElasticSearch } from '@interfaces/models/elasticSearch';
import Sort from '@components/catalog/sort/sort';
import Pagination from '@components/catalog/pagination/pagination';
import { useTranslation } from 'next-i18next';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import getCatalogQueryIndex from '@helpers/utils/catalog-query-index';
import { usePreferences } from '@context/preferences.context';
import dynamic from 'next/dynamic';
import { CatalogPageMetadataResponse } from '@interfaces/api/responses/catalog-page-metadata-response';
import SaveSearchButton from '@components/catalog/save-search-button/save-search-button';
import styles from './product-search/product-search.module.scss';

const CatalogHotfilters = dynamic(() => import('@components/catalog/catalog-hotfilters/catalog-hotfilters'), {
  ssr: false,
});
const CatalogTitleBlock = dynamic(() => import('@components/catalog/catalog-title-block/catalog-title-block'), {
  ssr: false,
});

type CurrentRefinementSectionProps = {
  isSortModalOpen: boolean;
  setIsSortModalOpen: Dispatch<SetStateAction<boolean>>;
  metadata: CatalogPageMetadataResponse | null;
  shouldHideCatalogHead?: boolean;
};

const CurrentRefinementSection: React.FC<CurrentRefinementSectionProps> = (props): React.JSX.Element => {
  const { isSortModalOpen, setIsSortModalOpen, metadata } = props;

  const { t } = useTranslation();
  const sortBy = useContextSelector(CatalogContext, (v) => v.sortBy);
  const updateCurrentPage = useContextSelector(CatalogContext, (v) => v.updateCurrentPage);
  const currentPage = useContextSelector(CatalogContext, (v) => v.currentPage);
  const pageCount = useContextSelector(CatalogContext, (v) => v.pageCount);
  const setSortBy = useContextSelector(CatalogContext, (v) => v.setSortBy);
  const isManualSwitchRef = useContextSelector(CatalogContext, (v) => v.isManualSwitchRef);
  const isPlpHotfiltersEnabled = useContextSelector(CatalogContext, (v) => v.isPlpHotfiltersEnabled);
  const { country } = usePreferences();
  const { sendAnalyticEvent } = useAnalyticEvents('catalog');
  const [alertError, setAlertError] = useState<string>('');

  const onSortChange = (type: ElasticSearch['sortBy']) => {
    sendAnalyticEvent('sorting_change', {
      action: getCatalogQueryIndex(country, type),
    });
    isManualSwitchRef.current = true;
    setSortBy(type);
  };

  return (
    <div className={styles.catalog__refinementSection}>
      {isPlpHotfiltersEnabled ? (
        <>
          <CatalogTitleBlock metadata={metadata} />
          <CatalogHotfilters />
        </>
      ) : (
        <>
          <CurrentRefinements />
          <div className={styles.catalog__paginationRow}>
            <SaveSearchButton setAlertError={setAlertError} />
            <div className={styles.catalog__sortActionBar}>
              <Sort
                onChange={(type) => onSortChange(type)}
                defaultValue={sortBy}
                openDialog={setIsSortModalOpen}
                open={isSortModalOpen}
                options={[
                  { text: t('CATALOG.SORT.RELEVANCE'), value: 'relevance' },
                  { text: t('CATALOG.SORT.PRICE_ASC'), value: 'price-asc' },
                  { text: t('CATALOG.SORT.PRICE_DESC'), value: 'price-desc' },
                  { text: t('CATALOG.SORT.MOST_RECENT'), value: 'recency' },
                ]}
              />
              <div className={styles.catalog__pagination}>
                <Pagination
                  behaviour="catalog"
                  currentPage={currentPage}
                  pageCount={pageCount}
                  showText={false}
                  hasArrows={true}
                  onChange={(page) => updateCurrentPage(page)}
                />
              </div>
            </div>
          </div>
          {alertError && <p className={styles.catalog__alertError}>{alertError}</p>}
        </>
      )}
    </div>
  );
};

export default CurrentRefinementSection;
